import { RoomType } from '../../types';

/**
 * STA-184 — property room coverage.
 *
 * Optional rooms (Office) only count toward the denominator when the property
 * actually has one, so existing four-room properties stay at 100%. Core rooms
 * are derived from the `RoomType` enum minus the optional ones — never
 * hard-code `/ 4` again. Unknown room types are ignored; result is 0–100.
 */
export const OPTIONAL_ROOMS: readonly RoomType[] = [RoomType.OFFICE];

const OPTIONAL_ROOM_SET = new Set<string>(OPTIONAL_ROOMS);

export const CORE_ROOMS: readonly RoomType[] = Object.values(RoomType).filter(
  (room) => !OPTIONAL_ROOM_SET.has(room),
);

const KNOWN_ROOM_SET = new Set<string>(Object.values(RoomType));

function knownRooms(covered: Iterable<string>): Set<string> {
  const seen = new Set<string>();
  for (const room of covered) {
    if (KNOWN_ROOM_SET.has(room)) seen.add(room);
  }
  return seen;
}

/** Rooms that count for this property: the core rooms plus any optional room it has. */
export function coverageRoomsFor(covered: Iterable<string>): RoomType[] {
  const seen = knownRooms(covered);
  return [...CORE_ROOMS, ...OPTIONAL_ROOMS.filter((room) => seen.has(room))];
}

/** Distinct known rooms present in `covered`. */
export function coveredRoomCount(covered: Iterable<string>): number {
  return knownRooms(covered).size;
}

/** Denominator for this property: core rooms + optional rooms it actually has. */
export function coverageDenominator(covered: Iterable<string>): number {
  return coverageRoomsFor(covered).length;
}

/** Coverage percentage, always within 0–100. */
export function roomCoveragePct(covered: Iterable<string>): number {
  const list = [...covered];
  const denominator = coverageDenominator(list);
  if (denominator === 0) return 0;
  return Math.min(100, Math.round((coveredRoomCount(list) / denominator) * 100));
}

export function isFullyCovered(covered: Iterable<string>): boolean {
  const list = [...covered];
  return coveredRoomCount(list) >= coverageDenominator(list);
}
